import cx from 'classnames'
import Styles from '../ElementsTab.module.scss'
import { useState, useEffect } from 'react'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { track } from '@tldraw/tldraw'
import { Section } from '../../TextTab/Section'
import { reportError } from 'utils/error'
import { MediaFile } from 'components/Design/UploadsTab/MediaFile'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { insertImage } from 'components/Design/UploadsTab/insertImage'
import { Icon, FreepikDownloadResponse, FreepikResponse } from './FreepikResponseType'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sanitizeAndUploadIcon } from './svgHelpers'
import { useCompleteDesignAction } from 'components/Design/utils/useCompleteDesignAction'
import { DesignActionType } from 'types'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

interface Props {
  query: string
}

const MAX_INITIAL_ICONS = 12

export const downloadIcon = async (id: number) => {
  const response = await sendToApi<FreepikDownloadResponse>(`/freepik/icons/download?id=${id}`, { method: 'GET' })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Failed to download icon')
  }

  const fileName = response.data.data.filename
  const sanitizedUrl = response.data.data.url.replace(/\\u([0-9a-fA-F]{4})/g, (_, grp) => {
    return String.fromCharCode(parseInt(grp, 16))
  })

  const icon = await fetch(sanitizedUrl)
  const blob = await icon.blob()
  const svgString = await blob.text()

  return sanitizeAndUploadIcon(svgString, fileName)
}

export const Freepik = track(
  observer(({ query }: Props) => {
    const editor = useEditor()
    const [icons, setIcons] = useState<Icon[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [showAll, setShowAll] = useState(false)
    const iconsToShow = showAll ? icons : icons.slice(0, MAX_INITIAL_ICONS)
    const completeActionMutation = useCompleteDesignAction()
    const { drawersStore } = useRootStore()

    const showNoResults = query.length > 0 && icons.length === 0 && !isLoading

    const loadIcons = async () => {
      try {
        setIsLoading(true)
        const response = await sendToApi<FreepikResponse>(`/freepik/icons?term=${query}`)

        if (response.type !== SUCCESS_RESPONSE_TYPE) {
          reportError(response, { severity: 'error' })
          return
        }

        const icons = response.data.data
        if (icons.length > 0) setIcons(icons)
      } catch (error) {
        reportError(error, { severity: 'error' })
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      setShowAll(false)
      setIcons([])
      loadIcons()
    }, [query]) // eslint-disable-line react-hooks/exhaustive-deps
    return (
      <Section.Root>
        <Section.Header>
          <Text variant="smallList">Freepik</Text>
          {!!query.length && !isLoading && icons.length > MAX_INITIAL_ICONS && (
            <Button variant="tertiary" size="xs" onPress={() => setShowAll(!showAll)}>
              {showAll ? 'See less' : 'See more'}
            </Button>
          )}
        </Section.Header>
        {iconsToShow.length > 0 && (
          <div className={cx(Styles.groupItems, Styles.dense)}>
            {iconsToShow.map((icon) => (
              <MediaFile
                key={icon.id}
                aria-label="Add this icon to the canvas"
                src={icon.thumbnails[0].url}
                file={
                  {
                    id: icon.id.toString(),
                    source_url: icon.thumbnails[0].url,
                    mime_type: 'image/svg+xml',
                    variants: [],
                  } as IMediaFile
                }
                payload={{
                  special_type: 'freepik',
                  special_payload: {
                    id: icon.id,
                  },
                }}
                onPress={async () => {
                  try {
                    const s3Url = await downloadIcon(icon.id)
                    insertImage(editor, {
                      id: s3Url,
                      source_url: s3Url,
                      mime_type: 'image/svg+xml',
                      variants: [],
                    } as IMediaFile)
                    completeActionMutation.mutate({ action: DesignActionType.CoreImage })
                    drawersStore.closeDrawer()
                  } catch (e) {
                    reportError(e, { severity: 'error' })
                  }
                }}
              />
            ))}
          </div>
        )}
        {showNoResults && (
          <Text variant="primary">
            No results found for <b>{query}</b>
          </Text>
        )}
      </Section.Root>
    )
  }),
)
